import React, { useEffect, useRef, useState } from 'react';
import { GoogleApiWrapper } from 'google-maps-react';
import { autocompleteListener } from './listeners/autocompleteListener';
import { mapClickListener } from './listeners/mapClickListener';
import { stringifyPolygon } from 'helpers/googleMap/stringifyCoordinate';
import { mapContainerStyles, polygonColors, polygonOpacity, strokeStyle } from 'styles/map';
import MapViewPolygonsController from 'controllers/MapViewPolygonsController';
import Grid from '@material-ui/core/Grid';
import styled from 'styled-components';

// components
import { BtnGray } from '../StyledComponents';
import { Map as GoogleMap, Polygon, InfoWindow, Marker } from 'google-maps-react';

// styled components
const MapOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: bold;
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  background-color: rgba(0,0,0,0.5);
  pointer-events: none;
`;

const getCenter = (arr) => {
    var x = arr.map (xy => xy[0]);
    var y = arr.map (xy => xy[1]);
    var cx = (Math.min (...x) + Math.max (...x)) / 2;
    var cy = (Math.min (...y) + Math.max (...y)) / 2;
    return {lat: cx, lng: cy};
}

const ControlledPolygon = (props) => {
  let {
    polygon,
    index,
    clickListener,
    isMain,
    isQualified,
    google,
    map,
    mapCenter,
    onMouseover
  } = props;

  let polygonRef = useRef();

  useEffect(() => {
    if(polygonRef && polygonRef.current)
      polygonRef.current.polygon.setOptions({
        fillColor: polygonColors[
          isMain ?
          'active' :
          'adjacentQualified'
        ]
      })
  }, [isQualified])

  return (
    <Polygon
      ref={polygonRef}
      google={google}
      map={map}
      mapCenter={mapCenter}
      paths={polygon.paths}
      onClick={index !== 0 ? clickListener : () => {}}
      strokeWeight={strokeStyle.weight}
      strokeColor={strokeStyle.color}
      onClick={index !== 0 ? clickListener : () => {}}
      fillColor={polygonColors.active}
      fillOpacity={polygonOpacity.active}
      zIndex={2}
      onMouseover={onMouseover}
    />
  );
}

const INITIAL_ZOOM = 13;

function Map({
  google,
  onAutocomplete,
  input,
  polygons,
  deselectTract,
  isOpened
}) {
  const mapRef = useRef(null);
  const infoRef = useRef(null);

  const [showInfo, setShowInfo] = useState(null);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if(isOpened === false && coords !== null) {
      setCoords(null);
      setShowInfo(false);
      fetchMapViewPolygons(mapRef.current.map, true)
    }
  }, [isOpened])

  const {
    mapViewPolygons,
    fetchMapViewPolygons,
    outsideError
  } = MapViewPolygonsController(INITIAL_ZOOM);

  const autoCompleteHandler = (place) => {
    const { map } = mapRef.current;
    const { lat, lng } =  place;
    setCoords({ lat, lng });
    onAutocomplete(place, google, map);
  };

  const clickListener = mapClickListener(google, autoCompleteHandler);
  useEffect(() => {
    autocompleteListener(google, input, autoCompleteHandler);
    applyUserLocation();
  }, []);

  function showInfoWindow(polygon) {
    let center = getCenter(polygon.paths.map(item => [item.lat, item.lng]));

    infoRef.current.infowindow.setOptions({
      position: center,
      visible: true
    })
    setShowInfo(polygon.tract);
  }

  async function applyUserLocation() {
    try {

      //finding current lat lng
      const URL = `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`;
      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      const {location: {lat, lng}} = await response.json();
      
      // creating latLng object
      const latLng = new google.maps.LatLng(lat, lng);
  
      // finding address of lat lng
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { latLng },
        (results) => {
          let {address_components} = results[0];
          let country = address_components.find(item => item.types.includes('country'))
          if(country && country.short_name === 'US') {
            mapRef.current.map.setCenter({lat, lng})
          }
        }
      )
      
    }
    catch(err) {
      console.log(err)
    }
  }

  return (
    <>
      <Grid container direction="column" alignItems="center" justify="center" style={{marginBottom: 5}}>
        {
          polygons.length > 0 &&
          <BtnGray
            small="true"
            onClick={() => {
              setCoords(null);
              setShowInfo(false);
              deselectTract();
              fetchMapViewPolygons(mapRef.current.map, true)
            }}
          >Reset Address</BtnGray>
        }
      </Grid>
      <div style={{position: 'relative'}}>
        <GoogleMap
          ref={mapRef}
          zoom={INITIAL_ZOOM}
          initialCenter={{ lat: 25.761681, lng: -80.191788 }}
          onIdle={() => fetchMapViewPolygons(mapRef.current.map)}
          onZoomChanged={() => setZoom(mapRef.current.map.getZoom())}
          google={google}
          containerStyle={mapContainerStyles}
          onClick={clickListener}
          className="map-wrapper"
        >
          {
            mapViewPolygons.length && mapViewPolygons.map((polygon, index) => (
              <Polygon
                key={`inView${stringifyPolygon(polygon)}${index}`}
                paths={polygon}
                strokeWeight={strokeStyle.weight}
                strokeColor={strokeStyle.inViewColor}
                fillColor={polygonColors.inView}
                fillOpacity={polygonOpacity.inView}
                onClick={clickListener}
                zIndex={1}
              />
            ))
          }
          {
            polygons.length && polygons.map((polygon, index) => (
              <ControlledPolygon
                key={`active${stringifyPolygon(polygon.paths)}${index}`}
                polygon={polygon}
                index={index}
                isMain={index === 0}
                isQualified={false}
                clickListener={clickListener}
                onMouseover={e => showInfoWindow(polygon)}
              />
            ))
          }
          {
            <InfoWindow
              ref={infoRef}
              visible={!!showInfo}
              onClose={() => setShowInfo(null)}
            >
                <div>
                  Tract {showInfo}
                </div>
            </InfoWindow>
          }
          {
            coords &&
            <Marker
              position={ coords }
            />
          }
        </GoogleMap>
        {
          ((zoom < 10 && polygons.length == 0) || outsideError) &&
          <MapOverlay>
            {
              zoom < 10 && polygons.length == 0 ?
              <div align="center" style={{maxWidth: 600}}>
                Zoom in to select your project’s location
              </div>:
              outsideError &&
              <div align="center" style={{maxWidth: 600}}>
                EB-5 TEAs are only located in the United States.
                <br/><br/>
                Please reposition the map or enter an address
                above that is in the United States.
              </div>
            }
          </MapOverlay>
        }
      </div>
    </>
  );
}

export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY
})(Map);
